import { AssignCommunitiesTable, Button, Textfield } from '@flexo/atoms';
import {
  IconWrapper,
  REGEX,
  getPrimaryOrigin,
  httpService,
} from '@flexo/general';
import { DrawerCloseButton } from '../drawer-close-button';
import { ALERT, METADATA, RIGHT_DRAWER } from '@flexo/reducers';
import { Player } from '@lottiefiles/react-lottie-player';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styles from './right-drawer-content.module.scss';

const loader = '/loaderLottie.json';

const ENV = (import.meta as any).env;

export function AdminAddNewUserDrawerContent() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const footerRef = useRef<HTMLDivElement>(null);

  // Keep footer width in sync with the drawer parent
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;
    const drawer = footer.closest('[class*="RightDrawer__AdminUser"]') as HTMLElement | null;
    if (!drawer) return;

    const sync = () => {
      footer.style.width = `${drawer.offsetWidth}px`;
    };
    sync();

    const ro = new ResizeObserver(sync);
    ro.observe(drawer);
    return () => ro.disconnect();
  }, []);
  const [role, setRole] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [selectedCommunities, setSelectedCommunities] = useState<Set<string>>(
    new Set()
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [inviteLanguage, setInviteLanguage] = useState<string>(
    i18n.language?.includes('_')
      ? i18n.language.split('_')[1]
      : i18n.language || 'it'
  );
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const metadata = useSelector((state: any) => state.metadata);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLangDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Role options with titles and descriptions
  const roleOptions = [
    {
      value: 'administrator',
      title: t('drawers.adminAddNewUser.inputs.role.options.administrator'),
      description: t(
        'drawers.adminAddNewUser.inputs.role.descriptions.administrator',
        {
          defaultValue:
            'Access to all permissions across all communities and the ability to manage other admin users.',
        }
      ),
    },
    {
      value: 'supervisor',
      title: t('drawers.adminAddNewUser.inputs.role.options.supervisor'),
      description: t(
        'drawers.adminAddNewUser.inputs.role.descriptions.supervisor',
        {
          defaultValue:
            'A community supervisor is a designated individual responsible for administering an energy community.',
        }
      ),
    },
  ];

  const selectedRoleOption = roleOptions.find((opt) => opt.value === role);

  // Language options for invite
  const languageOptions = [
    { value: 'it', title: 'Italiano' },
    { value: 'en', title: 'English' },
    { value: 'de', title: 'Deutsch' },
  ];

  const selectedLangOption = languageOptions.find(
    (opt) => opt.value === inviteLanguage
  );

  function handleSelectRole(value: string) {
    setRole(value);
    setIsDropdownOpen(false);
  }

  function handleSelectLanguage(value: string) {
    setInviteLanguage(value);
    setIsLangDropdownOpen(false);
  }

  function validateEmails(value: string): { valid: boolean; message: string } {
    const trimmed = value.trim();
    if (!trimmed) return { valid: true, message: '' };

    const emails = trimmed
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    const invalidEmails = emails.filter((e) => !REGEX.email.test(e));

    if (invalidEmails.length > 0) {
      return {
        valid: false,
        message:
          invalidEmails.length === 1
            ? t('drawers.adminAddNewUser.inputs.email.invalidEmail', {
                email: invalidEmails[0],
              })
            : t('drawers.adminAddNewUser.inputs.email.invalidEmails', {
                emails: invalidEmails.join(', '),
              }),
      };
    }
    return { valid: true, message: '' };
  }

  function handleEmailBlur() {
    const result = validateEmails(email);
    setEmailError(!result.valid);
    setEmailErrorMessage(result.message);
  }

  function handleEmailFocus() {
    setEmailError(false);
    setEmailErrorMessage('');
  }

  function closeDrawer() {
    dispatch(RIGHT_DRAWER.HideDrawer());
  }

  const allCommunities: any[] = useMemo(() => {
    const fromEntities = (metadata?.entities as any)?.community || [];
    const fromSlice = metadata?.communities || [];
    return (fromEntities?.length ? fromEntities : fromSlice) || [];
  }, [metadata]);

  const sites: any[] = useMemo(
    () => (metadata?.entities as any)?.site || [],
    [metadata]
  );
  const members: any[] = useMemo(
    () => (metadata?.entities as any)?.member || [],
    [metadata]
  );

  const countsByCommunity = useMemo(() => {
    const siteCount = new Map<string, number>();
    const memberCount = new Map<string, number>();
    sites.forEach((s: any) => {
      const key = s?.communityId;
      if (!key) return;
      siteCount.set(key, (siteCount.get(key) || 0) + 1);
    });
    members.forEach((m: any) => {
      const key = m?.communityId;
      if (!key) return;
      memberCount.set(key, (memberCount.get(key) || 0) + 1);
    });
    return { siteCount, memberCount };
  }, [sites, members]);

  const filteredCommunities = useMemo(() => {
    const s = (search || '').toLowerCase().trim();
    if (!s) return allCommunities;
    return allCommunities.filter((c: any) => {
      const name = (c?.communityName || '').toLowerCase();
      const code = (c?.communityCode || c?.communityId || '').toLowerCase();
      const extId = (c?.communityExternalId || '').toLowerCase();
      return name.includes(s) || code.includes(s) || extId.includes(s);
    });
  }, [allCommunities, search]);

  const allVisibleIds = useMemo(
    () => filteredCommunities.map((c: any) => c?.communityId).filter(Boolean),
    [filteredCommunities]
  );
  const isAllSelected =
    allVisibleIds.length > 0 &&
    allVisibleIds.every((id) => selectedCommunities.has(id));

  function toggleSelectAll() {
    if (isAllSelected) {
      const next = new Set(selectedCommunities);
      allVisibleIds.forEach((id) => next.delete(id));
      setSelectedCommunities(next);
    } else {
      const next = new Set(selectedCommunities);
      allVisibleIds.forEach((id) => next.add(id));
      setSelectedCommunities(next);
    }
  }

  function toggleRow(id: string) {
    const next = new Set(selectedCommunities);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedCommunities(next);
  }

  /**
   * Handle range toggle for shift+click selection
   * @param ids - Array of community IDs in the range
   * @param select - Whether to select or deselect the range
   */
  function toggleRange(ids: string[], select: boolean) {
    const next = new Set(selectedCommunities);
    ids.forEach((id) => {
      if (select) {
        next.add(id);
      } else {
        next.delete(id);
      }
    });
    setSelectedCommunities(next);
  }

  // Determine what to show based on selected role
  const showCommunitiesTable = role === 'supervisor';
  const showAdminMessage = role === 'administrator';

  // Check if form is valid
  const isFormValid = useMemo(() => {
    if (!email.trim() || !role) return false;
    if (role === 'supervisor' && selectedCommunities.size === 0) return false;
    const { valid } = validateEmails(email);
    return valid;
  }, [email, role, selectedCommunities]);

  // Handle invite submission - supports multiple emails separated by comma
  async function handleSubmit() {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);

    // Parse multiple emails separated by comma
    const emails = email
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    const results: { email: string; success: boolean; error?: string }[] = [];

    try {
      // Send one invite request per email
      for (const singleEmail of emails) {
        try {
          // Build the invite URL with the primary origin (not satellite)
          const inviteUrl = `${getPrimaryOrigin()}/register-by-invite?email=${encodeURIComponent(
            singleEmail
          )}`;

          // Build request payload
          const payload: any = {
            email: singleEmail,
            firstName: 'Invited', // Placeholder - will be updated during registration
            lastName: 'User',
            role: role,
            locale: inviteLanguage,
            language: inviteLanguage,
            inviteUrl: inviteUrl,
          };

          // Add communities only for supervisor role
          if (role === 'supervisor') {
            payload.communityIds = Array.from(selectedCommunities);
          }

          // Call the invite API (v1_4 endpoint)
          const response = await httpService.post({
            url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/organization/${ENV.VITE_APP_ORG_ID}/users`,
            data: payload,
            mock: false,
          });

          if (response.status === 201 || response.status === 200) {
            results.push({ email: singleEmail, success: true });
          } else {
            results.push({
              email: singleEmail,
              success: false,
              error: 'Unexpected response',
            });
          }
        } catch (err: any) {
          console.error(`Error sending invite to ${singleEmail}:`, err);
          const status = err?.response?.status;
          results.push({
            email: singleEmail,
            success: false,
            status: status,
            error:
              err?.response?.data?.detail?.[0]?.msg ||
              err?.message ||
              'Unknown error',
          });
        }
      }

      const successCount = results.filter((r) => r.success).length;
      const failedCount = results.filter((r) => !r.success).length;
      const alreadyExistsCount = results.filter(
        (r) => !r.success && r.status === 409
      ).length;

      // Optimistic: prepend placeholder rows for successful invites
      if (successCount > 0) {
        const roleDisplayMap: Record<string, string> = {
          administrator: 'Administrator',
          supervisor: 'Supervisor',
        };
        const formatDateDdMmYy = (dateStr: string): string => {
          try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yy = String(d.getFullYear()).slice(-2);
            return `${dd}/${mm}/${yy}`;
          } catch {
            return '';
          }
        };

        const optimisticRows = results
          .filter((r) => r.success)
          .map((r) => ({
            userId: null,
            clerkUserId: null,
            user: r.email,
            email: r.email,
            role: roleDisplayMap[role] || role,
            state: 'invited',
            activated: '',
            invited: formatDateDdMmYy(new Date().toISOString()),
            expiry: '',
            communities:
              role === 'administrator'
                ? 'All'
                : String(selectedCommunities.size),
            _raw: {},
            _optimistic: true,
          }));

        if (optimisticRows.length > 0) {
          dispatch(METADATA.PrependOptimisticInvites(optimisticRows));
        }
      }

      // Refresh both paginated tables if at least one invite succeeded
      if (successCount > 0) {
        dispatch(
          METADATA.SetAdminsPaginatedAsync({
            dispatch,
            organizationId: ENV.VITE_APP_ORG_ID,
            status: 'active',
            page: 1,
            pageSize: 50,
            sortBy: 'user',
            sortDir: 'asc',
            isRefresh: true,
          }) as any
        );
        dispatch(
          METADATA.SetAdminsPaginatedAsync({
            dispatch,
            organizationId: ENV.VITE_APP_ORG_ID,
            status: 'invited',
            page: 1,
            pageSize: 50,
            sortBy: 'invited',
            sortDir: 'desc',
            isRefresh: true,
          }) as any
        );
      }

      // Show appropriate alert based on results
      if (successCount > 0 && failedCount === 0) {
        // All succeeded
        dispatch(
          ALERT.SetAlert({
            type: 'BANNER_INVITE_USER_SUCCESS',
          } as any)
        );
        closeDrawer();
      } else if (successCount > 0 && failedCount > 0) {
        // Partial success - some invites sent, some failed
        const failedEmails = results
          .filter((r) => !r.success)
          .map((r) => r.email)
          .join(', ');
        console.warn(
          `Partial invite success: ${successCount} sent, ${failedCount} failed (${failedEmails})`
        );
        // Show partial success banner with failed emails
        dispatch(
          ALERT.SetAlert({
            value: {
              type: 'BANNER_INVITE_PARTIAL_SUCCESS',
              failedEmails: failedEmails,
              successCount: successCount,
              failedCount: failedCount,
            },
          } as any)
        );
        closeDrawer();
      } else if (alreadyExistsCount > 0) {
        // All failed because user(s) already exist
        dispatch(
          ALERT.SetAlert({
            type: 'BANNER_USER_ALREADY_INVITED',
          } as any)
        );
      } else {
        // All failed for other reasons
        dispatch(
          ALERT.SetAlert({
            type: 'BANNER_UNEXPECTED_ERROR',
          } as any)
        );
      }
    } catch (err: any) {
      console.error('Error sending invites:', err);
      dispatch(
        ALERT.SetAlert({
          type: 'BANNER_UNEXPECTED_ERROR',
        } as any)
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <div className={styles.AdminUser__Title__Wrapper}>
          <div className={styles.AdminUser__Title}>
            <p>{t('drawers.adminAddNewUser.title')}</p>
            <DrawerCloseButton onClose={closeDrawer} ariaLabel={t('common.close', { defaultValue: 'Close' })} />
          </div>
          <p className={styles.AdminUser__Subtitle}>
            {t('drawers.adminAddNewUser.subtitle')}
          </p>
        </div>

        <div className={styles.AdminUser__Body}>
          <Textfield
            textfieldColor="white"
            variant="default"
            name="email"
            value={email}
            placeholder="Email"
            label="Email"
            type="email"
            error={emailError}
            onChange={(e: any) => setEmail(e.target.value)}
            onBlur={handleEmailBlur}
            onFocus={handleEmailFocus}
            onPaste={(e) => false}
            onCopy={(e) => false}
            action={undefined}
            onActionClick={undefined}
            errorDescription={emailErrorMessage}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            autoComplete="off"
            extraDescription={String(
              t('drawers.adminAddNewUser.inputs.email.extraDescription')
            )}
            onIconClick={undefined}
          />

          {/* Language select for invite */}
          <div className={styles.AdminUser__Select} ref={langDropdownRef}>
            <span className={'heading4'}>
              {t('drawers.adminAddNewUser.inputs.language.label')}
            </span>
            <div
              className={styles.AdminUser__Select__Container}
              onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
            >
              <span className={styles.AdminUser__Select__Value}>
                {selectedLangOption?.title || 'Italiano'}
              </span>
              <div
                className={`${styles.AdminUser__Select__Icon} ${
                  isLangDropdownOpen
                    ? styles['AdminUser__Select__Icon--open']
                    : ''
                }`}
              >
                <IconWrapper iconName="chevron-down" />
              </div>
            </div>

            {isLangDropdownOpen && (
              <div className={styles.AdminUser__Select__Dropdown}>
                {languageOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`${styles.AdminUser__Select__Option} ${
                      inviteLanguage === option.value
                        ? styles['AdminUser__Select__Option--selected']
                        : ''
                    }`}
                    onClick={() => handleSelectLanguage(option.value)}
                  >
                    <span className={styles.AdminUser__Select__Option__Title}>
                      {option.title}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Role select - custom dropdown with title + description */}
          <div className={styles.AdminUser__Select} ref={dropdownRef}>
            <span className={'heading4'}>
              {t('drawers.adminAddNewUser.inputs.role.label')}
            </span>
            <div
              className={styles.AdminUser__Select__Container}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span
                className={
                  role
                    ? styles.AdminUser__Select__Value
                    : styles.AdminUser__Select__Placeholder
                }
              >
                {selectedRoleOption?.title ||
                  t('drawers.adminAddNewUser.inputs.role.placeholder')}
              </span>
              <div
                className={`${styles.AdminUser__Select__Icon} ${
                  isDropdownOpen ? styles['AdminUser__Select__Icon--open'] : ''
                }`}
              >
                <IconWrapper iconName="chevron-down" />
              </div>
            </div>

            {isDropdownOpen && (
              <div className={styles.AdminUser__Select__Dropdown}>
                {roleOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`${styles.AdminUser__Select__Option} ${
                      role === option.value
                        ? styles['AdminUser__Select__Option--selected']
                        : ''
                    }`}
                    onClick={() => handleSelectRole(option.value)}
                  >
                    <span className={styles.AdminUser__Select__Option__Title}>
                      {option.title}
                    </span>
                    <span
                      className={styles.AdminUser__Select__Option__Description}
                    >
                      {option.description}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Message for Administrator role */}
          {showAdminMessage && (
            <p
              className={styles.AdminUser__Subtitle}
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              {t('drawers.adminAddNewUser.messages.administrator', {
                defaultValue:
                  'Gli utenti di tipo amministratore possono accedere a tutte le comunità',
              })}
            </p>
          )}

          {/* Assign Communities - only for supervisor role */}
          {showCommunitiesTable && (
            <AssignCommunitiesTable
              communities={filteredCommunities}
              counts={countsByCommunity}
              selectedIds={selectedCommunities}
              isAllSelected={isAllSelected}
              onToggleRow={toggleRow}
              onToggleAll={toggleSelectAll}
              onRangeToggle={toggleRange}
              search={search}
              onSearchChange={(v) => setSearch(v)}
              labels={{
                title: t(
                  'drawers.adminAddNewUser.inputs.assign.title'
                ) as string,
                selectAll: t(
                  'drawers.adminAddNewUser.inputs.assign.selectAll'
                ) as string,
                searchPlaceholder: t(
                  'drawers.adminAddNewUser.inputs.assign.searchPlaceholder'
                ) as string,
                selectedCountText: t(
                  'drawers.adminAddNewUser.inputs.assign.selectedCount',
                  {
                    count: selectedCommunities.size,
                  }
                ) as string,
              }}
              classes={{
                wrapper: styles.AdminUser__Assign,
                header: styles.AdminUser__Assign__Header,
                headerActions: styles.AdminUser__Assign__Header__Actions,
                selectAll: styles.AdminUser__Assign__SelectAll,
                search: styles.AdminUser__Assign__Search,
                searchIcon: styles.AdminUser__Assign__Search__Icon,
                list: styles.AdminUser__Assign__List,
                listWrapper: styles.AdminUser__Assign__List__Wrapper,
                row: styles.AdminUser__Assign__Row,
                rowChecked: styles['AdminUser__Assign__Row--checked'],
                cellName: styles.AdminUser__Assign__Cell__Name,
                cellMuted: styles.AdminUser__Assign__Cell__Muted,
                cellCheck: styles.AdminUser__Assign__Cell__Check,
                footer: styles.AdminUser__Assign__Footer,
              }}
            />
          )}
        </div>
      </div>
      <div ref={footerRef} className={styles.AdminUser__Footer}>
        <Button
          color="secondary"
          label={
            isSubmitting ? (
              <Player
                autoplay
                loop
                src={loader}
                style={{ height: 20, transform: 'scale(0.7)' }}
              />
            ) : (
              (t('drawers.adminAddNewUser.inputs.submit') as string)
            )
          }
          className={styles.AdminUser__Footer__Button}
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
        />
      </div>
    </>
  );
}
